import get from "lodash.get";
import {
  applicationServicesClient
} from "./axios-client";
import * as FeePreference from "domain/FeePreference";
import * as Location from "domain/Location";
import {
  mapToUINotification,
  mapToCustomNotification
} from "mappers/notifications";

const withErrorHandling = fn => (...args) => {
  try {
    return fn(...args);
  } catch (err) {
    if (err.status) {
      throw err;
    }
    throw Error(`RequestError: ${err.message}`);
  }
};

// Location Services

async function _getStates() {
  const response = await applicationServicesClient.post(
    "/api/states?applyPreferences=false",
    {}
  );
  return response.data;
}

export const getStates = withErrorHandling(_getStates);

const mapResponseToStates = (response, country) => {
  const filterByCountry = Location.filterByCountry.bind(null, country);

  return get(response, "location.states", [])
    .filter(filterByCountry)
    .map(Location.normalizeState);
};

async function _getCountryStates(countryCode = "US") {
  const responseData = await _getStates();
  return mapResponseToStates(responseData, countryCode);
}

export const getStatesByCountry = withErrorHandling(_getCountryStates);

// Preferences

async function _getFeePreferences() {
  const response = await applicationServicesClient.get("/api/preferences");
  return response.data;
}

export const getFeePreferences = withErrorHandling(_getFeePreferences);

const mapResponseToFeesPreferences = (response, state) => {
  const filterByState = FeePreference.filterByState.bind(null, state);
  const statePreferences = response.preferences.feesPreferences
    .filter(filterByState);
  statePreferences.map( feePreferences =>
      feePreferences.feeNamePreferences.map( feePreference => {
          feePreference.isEnabled = feePreference.isEnabled === 'True' ? true : false;
          feePreference.payType = feePreference.payType === 0 ? "Full" : "Prorated";
      }
    )
  )

  return FeePreference.groupByFeeType(statePreferences);
};

async function _getFeesPreferencesByStateCode(stateCode) {
  const responseData = await _getFeePreferences();
  return mapResponseToFeesPreferences(responseData, stateCode);
}

export const getFeesPreferencesByStateCode = withErrorHandling(
  _getFeesPreferencesByStateCode
);

export const getFeeDescriptionsByState = withErrorHandling(async stateCode => {
  const response = await applicationServicesClient.get(
    `/api/GetFeeNames?stateCode=${stateCode}`
  );
  return (response.data || []).reduce((acc, x) => ({ ...acc, ...x }), {});
});

export const getFeeReferencesByState = withErrorHandling(async stateCode => {
  const response = await applicationServicesClient.get(
    `/api/GetFeeReferences?stateCode=${stateCode}`
  );
  return (response.data || []).reduce((acc, x) => ({ ...acc, [x.feeName]: x.externalData }), {});
});

export const getFeeNotifications = withErrorHandling(
  async ({ cancelToken } = {}) => {
    const response = await applicationServicesClient.get(
      "/api/GetNotifications",
      { cancelToken }
    );
    const data = get(response, "data", []);
    return data.map(mapToUINotification);
  }
);

export const markFeeNotificationAsRead = withErrorHandling(id => {
  return applicationServicesClient.put("/api/Notifications", null, {
    params: { id, status: "read", value: true }
  });
});

export const removeFeeNotification = withErrorHandling(id => {
  return applicationServicesClient.put("/api/Notifications", null, {
    params: { id, status: "show", value: false }
  });
});

export const getArchiveFeeNotifications = withErrorHandling(
  async ({ cancelToken } = {}) => {
    const response = await applicationServicesClient.get(
      "/api/FeesNotifications",
      { cancelToken }
    );
    const data = get(response, "data", []);
    return data.map(mapToUINotification);
  }
);

export const getPreferences = withErrorHandling(
  async ({ cancelToken } = {}) => {
    const response = await applicationServicesClient.get("/api/preferences", {
      cancelToken
    });
    const data = get(response, "data.preferences", {});
    return data;
  }
);
export const getCustomNotifications = withErrorHandling(
  async ({ cancelToken, showAll } = {}) => {
    const response = await applicationServicesClient.get(
      "/api/GetCustomNotifications",
      { cancelToken, params: { ShowAll: showAll } }
    );

    return get(response, "data", []).map(mapToCustomNotification);
  }
);

export const markCustomNotificationAsRead = withErrorHandling(id => {
  return applicationServicesClient.put(
    "/api/Notifications",
    null,
    {
      params: {
        id,
        status: "read",
        value: true
      }
    }
  );
});

export const removeCustomNotification = withErrorHandling(id => {
  return applicationServicesClient.put(
    "/api/Notifications",
    null,
    {
      params: {
        id,
        status: "show",
        value: false
      }
    }
  );
});

export const combineNotifications = (...fns) => async (...args) => {
  const promises = fns.map(async fn => {
    try {
      const result = await fn(...args);
      return { value: result };
    } catch (err) {
      return { error: err };
    }
  });

  const results = await Promise.all(promises);
  const notifications = results
    .filter(result => result && result.value)
    .reduce((arr, result) => [...arr, ...result.value], [])
    .sort((a, b) => b.createdAt - a.createdAt);

  return notifications;
};

export const getArchiveNotifications = combineNotifications(
  getArchiveFeeNotifications,
  args => getCustomNotifications({ ...args, showAll: true })
);
export const getNotifications = combineNotifications(
  getFeeNotifications,
  getCustomNotifications
);

export const markNotificationAsRead = notification => {
  return notification.type === "custom"
    ? markCustomNotificationAsRead(notification.id)
    : markFeeNotificationAsRead(notification.id);
};

export const removeNotification = notification => {
  return notification.type === "custom"
    ? removeCustomNotification(notification.id)
    : removeFeeNotification(notification.id);
};

export const fetchPdf = withErrorHandling(
  ({ cancelToken, workItemId, type }) => {
    return applicationServicesClient.get(
      `api/document?workItemId=${workItemId}&type=${type}`,
      { cancelToken, responseType: "blob" }
    );
  }
);
